import { ImageViewerComponent } from '@addins/callcard/components/image-viewer/image-viewer.component';
import { DownloadUrlTransformService } from '@addins/core/file';
import { CallcardStampsService, StampsProxyService } from '@addins/core/journal';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { MediaType } from '@models/imported/SagaBase';
import { DataType } from '@models/imported/SagaBase/Interfaces';
import { CAttachment } from '@models/imported/SagaSchema/CAttachment';
import { CStampFreeText } from '@models/imported/SagaSchema/CStampFreeText';
import { CLocationHistory } from '@models/imported/SagaSchema/History/CLocationHistory';
import { CMediaMessage } from '@models/imported/TW/Saga/Core/Schema/Callcard/CMediaMessage';
import { Events } from '@services/events/events.service';
import { ModalService } from '@services/modal/modal.service';
import { NavService } from '@services/navigation/navigation.service';
import { Security } from '@services/security/security.service';
import { Tool, ToolsProvider, ToolsProviderService } from '@services/tools';
import { TranslatedAlertService } from '@services/translated-alert/translated-alert.service';
import { ViewConfigurationService } from '@services/view-configuration/view-configuration.service';
import { ADDIN_VIEW_EVENT_KEY, AddinExecute, Execute } from '@techwan/ionic-core';
import { Subscription } from 'rxjs';

@AddinExecute({
  name: 'Tools',
  description: 'Provide some tools for the followup stamps'
})
@Injectable()
export class ToolsService extends Execute {
  private editTool: Tool;
  private mediaViewer: Tool;
  private mediaDownload: Tool;
  private locationViewer: Tool;

  private unregisterMediaViewer: Subscription;
  private unregisterEditStamp: Subscription;
  private unregisterLocationViewer: Subscription;

  constructor(
    private toolsProvider: ToolsProviderService,
    private modalCtrl: ModalController,
    private security: Security,
    private modal: ModalService,
    private navService: NavService,
    private events: Events,
    private view: ViewConfigurationService,
    private downloadUrlTransformService: DownloadUrlTransformService,
    private stampsProxy: StampsProxyService,
    private callcardStamps: CallcardStampsService,
    private translatedAlertService: TranslatedAlertService
  ) {
    super();
  }

  init() {
    this.createTools();
  }

  private createTools() {
    this.editTool = Tool.createTool({
      text: 'Edit',
      execute: async context => {
        if (context[0] instanceof CStampFreeText) {
          const stamp: CStampFreeText = context[0];
          this.editFreeText(stamp.SourceComponentUid);
        }
      },
      icon: 'create'
    });

    this.mediaViewer = Tool.createTool({
      text: 'Open',
      icon: 'open',
      priority: 100,
      execute: async context => {
        const attachment: CAttachment | CMediaMessage = context[0];
        const url: string = this.getMediaUrl(attachment);
        const caption: string = this.getMediaTitle(attachment);

        this.modalCtrl
          .create({
            component: ImageViewerComponent,
            componentProps: {
              image: {
                url,
                caption
              }
            }
          })
          .then(m => m.present());
      }
    });

    this.mediaDownload = Tool.createTool({
      text: 'Download',
      icon: 'download',
      priority: 100,
      execute: async context => {
        const attachment: CAttachment | CMediaMessage = context[0];
        const url: string = this.getMediaUrl(attachment);

        // Download it
        this.security
          .get({
            responseType: 'blob',
            url
          })
          .then((data: Blob) => {
            const blobUrl = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style.display = 'none';
            a.href = blobUrl;
            a.setAttribute('download', this.getMediaTitle(attachment));
            a.setAttribute('target', '_blank');
            a.click();
            document.body.removeChild(a);
          });
      }
    });

    this.locationViewer = Tool.createTool({
      text: 'Map',
      icon: 'map',
      priority: 100,
      execute: context => {
        this.navService.getViewOpen(this.view.getMapName()).then(mapView => {
          if (mapView) {
            mapView.centerOnLocation(context[0], 1);
          } else {
            this.events.publish(ADDIN_VIEW_EVENT_KEY, this.view.getMapName(), {
              location: context[0]
            });
          }
        });
      }
    });
  }

  private editFreeText(objGuid: string) {
    this.showEditFreeText().then(text => this.updateFreeText(objGuid, text));
  }

  private showEditFreeText(): Promise<string> {
    return new Promise<string>(resolve => {
      this.translatedAlertService.show({
        header: 'SagaMobile.Stamp.Freetext.Title',
        message: 'SagaMobile.Stamp.Freetext.Message',
        inputs: [
          {
            name: 'text',
            placeholder: 'SagaMobile.Stamp.Freetext.PlaceHolder',
            min: 1,
            max: 200
          }
        ],
        buttons: [
          {
            text: 'SagaMobile.Stamp.Freetext.Cancel',
            value: false
          },
          {
            text: 'SagaMobile.Stamp.Freetext.Send',
            value: true,
            handler: data => {
              if (data && data.text) {
                resolve(data.text);
              }
            }
          }
        ]
      });
    });
  }

  private updateFreeText(objGuid: string, text: string) {
    this.modal.presentLoading('SagaMobile.Stamp.Freetext.Sending').then(() => {
      this.stampsProxy
        .readFreetext(objGuid)
        .toPromise()
        .then(freeText => {
          freeText.Text = text;
          this.stampsProxy
            .updateFreetext(freeText)
            .toPromise()
            .then(response => {
              if (response) {
                this.callcardStamps.$refreshStamps.emit();
              }
            })
            .finally(() => this.modal.dismissLoading());
        });
    });
  }

  private getMediaUrl(attachment: CAttachment | CMediaMessage): string {
    return this.downloadUrlTransformService.transform(attachment.Url);
  }

  private getMediaTitle(attachment: CAttachment | CMediaMessage): string {
    return attachment instanceof CAttachment ? attachment.Title : attachment.Text;
  }

  execute(): void {
    this.registerTools();
  }

  private registerTools() {
    this.unregisterMediaViewer = this.toolsProvider.register(toolsProvider => {
      if (toolsProvider.context.length === 1) {
        if (toolsProvider.contextObjectsAre(CAttachment)) {
          this.registerAttachmentTools(toolsProvider);
        } else if (toolsProvider.contextObjectsAre(CMediaMessage)) {
          this.registerMediaMessageTools(toolsProvider);
        }
      }
    });

    this.unregisterEditStamp = this.toolsProvider.register(toolsProvider => {
      if (toolsProvider.context.length === 1 && toolsProvider.contextObjectsAre(CStampFreeText)) {
        toolsProvider.addTool(this.editTool);
      }
    });

    this.unregisterLocationViewer = this.toolsProvider.register(toolsProvider => {
      if (toolsProvider.context.length === 1 && toolsProvider.contextObjectsAre(CLocationHistory)) {
        toolsProvider.addTool(this.locationViewer);
      }
    });
  }

  private registerAttachmentTools(toolsProvider: ToolsProvider) {
    const attachment: CAttachment = toolsProvider.context[0];
    switch (attachment.BinaryDataType) {
      case DataType.Picture:
        toolsProvider.addTool(this.mediaViewer);
        break;
      default:
        toolsProvider.addTool(this.mediaDownload);
        break;
    }
  }

  private registerMediaMessageTools(toolsProvider: ToolsProvider) {
    const mediaMessage: CMediaMessage = toolsProvider.context[0];
    switch (mediaMessage.MediaType) {
      case MediaType.Image:
        toolsProvider.addTool(this.mediaViewer);
        break;
      case MediaType.Document:
      case MediaType.Video:
        toolsProvider.addTool(this.mediaDownload);
        break;
    }
  }

  destroy(): void {
    if (this.unregisterMediaViewer) {
      this.unregisterMediaViewer.unsubscribe();
      this.unregisterMediaViewer = null;
    }
    if (this.unregisterEditStamp) {
      this.unregisterEditStamp.unsubscribe();
      this.unregisterEditStamp = null;
    }
    if (this.unregisterLocationViewer) {
      this.unregisterLocationViewer.unsubscribe();
      this.unregisterLocationViewer = null;
    }
  }
}
